"use client";

import cn from "classnames";
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";

import { BaseButton } from "@/src/component/base/BaseButton";
import { I18NDropdown } from "@/src/component/partial/I18NDropdown";
import { MarketingNav } from "@/src/component/partial/Navigation";
import { NoSSR } from "@/src/component/partial/NoSSR";
import { NotificationPopup } from "@/src/component/partial/NotificationPopup";
import { Nudge } from "@/src/component/partial/Nudge";
import { Preload } from "@/src/component/partial/Preload";
import { WaveMenuButton } from "@/src/component/partial/WaveMenuButton";
import { DEFAULT_ROUTE } from "@/src/const";
import { BrowserHooks, ProviderHooks, TrackingHooks } from "@/src/hook";
import * as BrokerProfileLogic from "@/src/logic/BrokerProfileLogic";
import { FrontendTrackingService } from "@/src/service";
import { Nudge as NudgeType } from "@/src/type/strapi/component/object";
import type { StrapiSingleType, StrapiCollectionType, StrapiComponentType } from "@/src/type";
import { StrapiAsset, StrapiAssetAttributes } from "@/src/type/strapi/StrapiType";
import { FormHelper } from "@/src/util";

import { HeaderButton } from "./component";

import styles from "./Header.module.scss";

function getPhoneNumberLabel(
  property?: StrapiCollectionType.Property,
  marketingPhoneNumber?: {
    phoneNumber?: string;
    countryCode?: string;
  }
) {
  const phoneNumber = marketingPhoneNumber?.phoneNumber ?? property?.phoneNumber;
  const countryCode = marketingPhoneNumber?.countryCode ?? property?.countryCode;
  if (!phoneNumber) {
    return undefined;
  }

  //US phone number
  if (!countryCode || countryCode === "1") {
    const label = FormHelper.displayPhoneNumber(phoneNumber);
    const labelParts = label ? `${label}`.split("-") : [];
    const formattedLabel = labelParts.length > 2 ? `(${labelParts[0]}) ${labelParts[1]}-${labelParts[2]}` : label;
    return {
      href: `tel:${phoneNumber}`,
      label: formattedLabel
    };
  }

  //TODO: add a formating option for the phone number, make this better
  const label = FormHelper.displayPhoneNumber(phoneNumber, 2, 5);
  return {
    href: `tel:${countryCode}${phoneNumber}`,
    label: `+${countryCode}-${label}`,
  };
}

interface HeaderProps {
  webConfig: StrapiSingleType.WebConfig;
  page?: StrapiCollectionType.DynamicPage;
  nav?: StrapiCollectionType.Navigation;
  domainData?: StrapiCollectionType.Domain;
  marketingSources?: StrapiCollectionType.MarketingSource[];
  homePath?: string;
}

function Header({ webConfig, page, nav, domainData, marketingSources, homePath }: HeaderProps) {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const app = ProviderHooks.useApp();
  const cms = ProviderHooks.useCMS();
  const externalLink = BrowserHooks.useLinkWithTrackingParams(page?.contactUsLink ?? "", true);
  const promoNudge = page?.promo?.data?.attributes?.promoControl?.find((item) => (item as NudgeType).name === "nudge");
  const brand = (page?.flowLogoOverride ?? domainData?.brand ?? "Flow").toLowerCase();
  const marketingPhoneNumber = TrackingHooks.useMarketingPhoneNumber({
    marketingSources: marketingSources ?? [],
    propertySlug: page?.property?.data?.attributes?.slug ?? "",
  });
  const phoneNumberLabel = getPhoneNumberLabel(page?.property?.data?.attributes, marketingPhoneNumber);
  const phoneNumberCta = phoneNumberLabel?.href && !page?.hidePhoneNumber ? [{
    href: phoneNumberLabel?.href,
    label: phoneNumberLabel?.label,
    iconLight: getPhoneIcon("light"),
    iconDark: getPhoneIcon("dark"),
    target: "_self",
    isExternal: true,
    name: "Phone Number",
    showIconBreakpoint: "belowMD",
  }] : [];
  const ctas = [...phoneNumberCta, ...(page?.headerCta ?? [])] as StrapiComponentType.Element.ButtonLink[];

  function getLogo(): string {
    switch (app?.header?.flowLogo ?? "default") {
      case "dark":
      case "color":
      case "flow-house-dark":
        switch (brand) {
          case "flow":
            return "/image/logo_flow_dark.svg";
          case "riyadh":
            return "/image/logo_flow_riyadh_dark.png";
          case "trace":
            return "/image/trace/logo_dark.png";
          default:
            return "/image/logo_flow_house_dark.svg";
        }
      case "light":
      case "flow-house-light":
      case "white":
      default:
        switch (brand) {
          case "flow":
            return "/image/logo_flow_white.svg";
          case "riyadh":
            return "/image/logo_flow_riyadh_light.png";
          case "trace":
            return "/image/trace/logo_light.png";
          default:
            return "/image/logo_flow_house_light.svg";
        }
    }
  }

  function getLogoSize() {
    switch (brand) {
      case "flow":
        return { width: 180, height: 28 };
      case "riyadh":
        return { width: 161, height: 28 };
      case "trace":
        return { width: 98, height: 28 };
      case "flowhouse":
      default:
        return { width: 208, height: 32 };
    }
  }

  function getEmailIcon() {
    return app?.header?.darkMenuIcon
      ? "/image/icon_email_header_dark.svg"
      : "/image/icon_email_header.svg";
  }


  function getPhoneIcon(theme: "light" | "dark"): StrapiAsset {
    return {
      data: {
        id: 0,
        attributes: {
          url: theme === "dark"
            ? "/image/icon_phone_dark.svg"
            : "/image/icon_phone_light.svg",
          width: 24,
          height: 24,
        } as StrapiAssetAttributes,
      },
    };
  }

  function showContactUsButton() {
    if (BrokerProfileLogic.checkQueryStringHasProfile(app.defaultQuery)) {
      return false;
    }
    return !!(page?.contactUsLink && page?.contactUsButtonText);
  }

  function isExternal() {
    return page?.contactUsLink?.includes("https://");
  }

  const preload = domainData?.brand === "Flow" ? [
    "/image/logo_flow_dark.svg",
    "/image/logo_flow_white.svg",
  ] : [
    "/image/logo_flow_house_light.svg",
    "/image/logo_flow_house_dark.svg",
  ];

  return (
    <>
      <Preload
        images={[
          "/image/icon_email_header_dark.svg",
          "/image/icon_email_header.svg",
          "/image/icon_menu_dark.svg",
          "/image/icon_menu.svg",
          "/image/icon_flow_wave_light.svg",
          "/image/icon_flow_wave.svg",
          ...preload
        ]}
      />
      <header
        className={cn(
          styles.headerSticky,
          styles.header,
          app?.header?.bottomShadow ? styles.shadow : undefined,
          app?.header?.blurBackground ? styles.blurBackground : undefined
        )}
        style={{
          backgroundColor: !app?.header?.inheritBackgroundColorOff
            ? app.backgroundColor
            : "transparent",
        }}
      >
        {!app?.header?.navOff && (
          <div className={styles.inner}>
            <div className={styles.menu} onClick={() => setDrawerOpen(!drawerOpen)} onTouchStart={(e) => e.stopPropagation()}>
              <WaveMenuButton darkMenuIcon={app?.header?.darkMenuIcon} />
            </div>
            {!app?.header?.logoOff && app?.header?.flowLogo && (
              <Link
                className={cn(
                  styles.logo,
                  getLogoSize().width === 208 ? styles.flowhouse : undefined
                )}
                onTouchStart={(e) => e.stopPropagation()}
                href={cms.constructLink(homePath ?? `/${DEFAULT_ROUTE}`)}
                title="Home"
                aria-label="Home"
              >
                <Image
                  src={getLogo()}
                  width={getLogoSize().width}
                  height={getLogoSize().height}
                  alt="Flow Logo"
                  priority={true}
                />
              </Link>
            )}
            <div className={cn(styles.otherCta, page?.showNotifications ? styles.showNotifications : undefined)}>
              <NoSSR>
                {!!promoNudge && (
                  <Nudge {...promoNudge} />
                )}
              </NoSSR>
              {page?.showNotifications && (
                <div className={cn(styles.notification, app?.header?.darkMenuIcon ? styles.dark : undefined)}>
                  <NotificationPopup isDark={app?.header?.darkMenuIcon} />
                </div>
              )}
              {domainData?.i18n && (
                <div className={styles.i18nDropdown}>
                  <I18NDropdown color={app?.header?.darkMenuIcon ? "dark" : "light"} domainData={domainData} />
                </div>
              )}
              {!!ctas?.length && (
                <div className={cn(styles.headerCtaButtons)}>
                  {(ctas ?? []).map((cta, i) => (
                    <HeaderButton
                      key={i}
                      buttonLink={cta}
                      darkMenuIcon={app?.header?.darkMenuIcon}
                      alwaysShowIcon={cta.name === "Phone Number"}
                      hideOverride={cta.name === "Phone Number" ? page?.hidePhoneNumber : undefined}
                    />
                  ))}
                </div>
              )}
              {/*This is old Flow House Contact Us Button, should be removed*/}
              {showContactUsButton() && (
                <Link
                  href={isExternal() ? externalLink : cms.constructLink(page?.contactUsLink ?? "")}
                  target={isExternal() ? "_blank" : "_self"}
                  shallow={!isExternal()}
                >
                  <BaseButton
                    id={`header.contactUsButton_${page?.slug}`}
                    aria-label={page?.contactUsButtonText ?? "Contact us button"}
                    className={cn(styles.ctaLargeScreen, page?.contactUsButtonMobile === "buttonWithText" ? styles.buttonWithText : styles.icon)}
                    variant="outlined"
                    size="large"
                    color={app?.header?.darkMenuIcon ? "primary" : "secondary"}
                    onClick={() => {
                      FrontendTrackingService.postTrackingLog({
                        action: "click",
                        actionId: page?.slug,
                        actionIdType: "pageSlug",
                        actionMessage: page?.contactUsButtonText ?? "Contact us button",
                        triggeredFrom: "Header.tsx:(onClick contact us button)",
                        pushTo: {
                          googleAnalytics: "contactus_click",
                          googleAds: true,
                          hubspot: true,
                          meta: "contactus_click",
                        }
                      });
                    }}
                  >
                    {page?.contactUsButtonText ?? "Live in Flow"}
                  </BaseButton>
                  <Image
                    className={cn(styles.ctaSmallScreen, page?.contactUsButtonMobile === "buttonWithText" ? styles.buttonWithText : styles.icon)}
                    src={getEmailIcon()}
                    width={34}
                    height={34}
                    alt="Contact Us Logo"
                    priority={true}
                  />
                </Link>
              )}
            </div>
          </div>
        )}
      </header>
      <MarketingNav drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} webConfig={webConfig} nav={nav} />
    </>
  );
}

export default Header;
